import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import ScrubberReport from "./ScrubberReport";

// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────

interface BuiltInPattern {
  name: string;
  description: string;
  replacement: string;
  contextRequired: boolean;
}

interface ScrubberData {
  enabled: boolean;
  patterns: BuiltInPattern[];
}

interface CustomPattern {
  id: number;
  name: string;
  description?: string;
  pattern: string;
  replacement: string;
  isEnabled: boolean;
  createdBy: string;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────

export default function MessageScrubbingPanel() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const hasScrubbing = subscription?.enableMessageScrubbing ?? false;

  const [scrubberData, setScrubberData] = useState<ScrubberData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchBuiltIns = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/scrubber");
      if (resp.ok) setScrubberData(await resp.json());
    } catch {
      // graceful default
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { fetchBuiltIns(); }, [fetchBuiltIns]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cornflower-blue-500" />
        <span className="ml-3 text-slate-500 text-sm">Loading...</span>
      </div>
    );
  }

  if (!hasScrubbing) {
    return (
      <div className="max-w-2xl p-6">
        <h2 className="text-xl font-bold text-slate-800">Message Scrubbing</h2>
        <p className="text-sm text-slate-500 mt-1">
          Automatically detect and redact sensitive data (credit cards, SSNs, custom patterns) from
          chat messages before they are stored or seen by agents.
        </p>
        <div className="mt-6 bg-slate-50 rounded-lg border border-slate-200 p-5">
          <p className="font-medium text-slate-700">Not included in your current plan</p>
          <p className="mt-1 text-sm text-slate-500">
            Message scrubbing is a compliance feature. Contact{" "}
            <a href="mailto:sales@velaro.com" className="text-cornflower-blue-600 underline">
              sales@velaro.com
            </a>{" "}
            to enable it.
          </p>
        </div>
      </div>
    );
  }

  const patterns = Array.isArray(scrubberData?.patterns) ? scrubberData!.patterns : [];
  const isEnabled = scrubberData?.enabled ?? false;

  return (
    <div className="max-w-4xl p-6 space-y-8">
      {/* ── Header ── */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">Message Scrubbing</h2>
        <p className="text-sm text-slate-500 mt-1">
          Automatically detect and redact sensitive data from chat messages in real time.
        </p>
      </div>

      {/* ── Status banner ── */}
      <div className={`rounded-lg border p-4 flex items-center gap-3 ${
        isEnabled ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
      }`}>
        <span className={`w-3 h-3 rounded-full shrink-0 ${isEnabled ? "bg-emerald-500" : "bg-amber-500"}`} />
        <div>
          <span className={`text-sm font-semibold ${isEnabled ? "text-emerald-800" : "text-amber-800"}`}>
            {isEnabled ? "Active" : "Inactive"}
          </span>
          <span className={`text-sm ml-2 ${isEnabled ? "text-emerald-600" : "text-amber-600"}`}>
            {isEnabled
              ? "Messages are being scanned for sensitive data in real time."
              : "Scrubbing is enabled on your plan but not yet active for this site. Contact your administrator."}
          </span>
        </div>
      </div>

      {/* ── Built-in patterns ── */}
      <section>
        <h3 className="font-semibold text-slate-800 mb-1">Built-in Detection Patterns</h3>
        <p className="text-sm text-slate-500 mb-4">
          These run automatically on every message and cannot be customized.
        </p>
        {patterns.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No built-in pattern data returned from the scrubber service.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {patterns.map((p, i) => <BuiltInCard key={i} pattern={p} />)}
          </div>
        )}
      </section>

      {/* ── How it works ── */}
      <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-5">
        <h3 className="font-semibold text-cornflower-blue-800 mb-2">How it works</h3>
        <ul className="text-sm text-cornflower-blue-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">1.</span>
            <span>When a visitor or agent sends a message, the scrubber checks it against all active patterns.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">2.</span>
            <span>Matched sensitive data is replaced with a redaction tag (e.g., [redacted card]) before the message is stored or displayed.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">3.</span>
            <span>The original sensitive data is never stored in the database or shown to agents.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">4.</span>
            <span>Custom regex patterns let you extend coverage for business-specific data (employee IDs, account numbers, etc.).</span>
          </li>
        </ul>
      </div>

      <hr className="border-slate-200" />

      {/* ── Custom patterns ── */}
      <CustomPatternsSection api={api} />

      <hr className="border-slate-200" />

      {/* ── Scrubber Report ── */}
      <ScrubberReport />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Built-in pattern card (read-only)
// ─────────────────────────────────────────────────────────────────

function BuiltInCard({ pattern }: { pattern: BuiltInPattern }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold text-slate-800">{pattern.name}</span>
        {pattern.contextRequired && (
          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
            Context-aware
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-2">{pattern.description}</p>
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-slate-400">Replaced with:</span>
        <code className="text-xs bg-slate-100 text-crimson-red-600 px-1.5 py-0.5 rounded">
          {pattern.replacement}
        </code>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Custom regex patterns — full CRUD
// ─────────────────────────────────────────────────────────────────

function CustomPatternsSection({ api }: { api: ReturnType<typeof useApi> }) {
  const toastAlert = useToastAlert();
  const [patterns, setPatterns] = useState<CustomPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CustomPattern | null>(null);

  const fetchPatterns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.admin.get("Scrubber/patterns");
      if (res.ok) {
        const data = await res.json();
        setPatterns(Array.isArray(data) ? data : []);
      }
    } catch {
      setPatterns([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchPatterns(); }, [fetchPatterns]);

  const handleToggleEnabled = async (p: CustomPattern) => {
    try {
      const res = await api.admin.put(`Scrubber/patterns/${p.id}`, {
        name: p.name,
        description: p.description,
        pattern: p.pattern,
        replacement: p.replacement,
        isEnabled: !p.isEnabled,
      });
      if (res.ok) {
        setPatterns((prev) => prev.map((x) => x.id === p.id ? { ...x, isEnabled: !x.isEnabled } : x));
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to update pattern");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to update pattern");
    }
  };

  const handleDelete = async (p: CustomPattern) => {
    if (!confirm(`Delete custom pattern "${p.name}"? This cannot be undone.`)) return;
    try {
      const res = await api.admin.delete(`Scrubber/patterns/${p.id}`);
      if (res.ok) {
        setPatterns((prev) => prev.filter((x) => x.id !== p.id));
        toastAlert.displayToast(Severity.Success, "Pattern deleted");
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to delete pattern");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to delete pattern");
    }
  };

  const openAdd = () => { setEditing(null); setShowModal(true); };
  const openEdit = (p: CustomPattern) => { setEditing(p); setShowModal(true); };

  return (
    <section>
      <div className="flex items-center justify-between mb-1">
        <div>
          <h3 className="font-semibold text-slate-800">Custom Regex Patterns</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Add your own patterns to catch business-specific data (employee IDs, account codes, etc.).
            Patterns are applied in addition to the built-in rules.
          </p>
        </div>
        <button
          className="shrink-0 px-3 py-1.5 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600"
          onClick={openAdd}
        >
          + Add Pattern
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-400 text-sm">Loading custom patterns...</div>
      ) : patterns.length === 0 ? (
        <div className="mt-4 border border-dashed border-slate-300 rounded-lg py-8 text-center">
          <p className="text-slate-400 text-sm">No custom patterns yet.</p>
          <p className="text-slate-300 text-xs mt-1">
            Add a regex pattern to extend scrubbing beyond the built-in rules.
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Pattern</th>
                <th className="px-3 py-2">Replacement</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Added By</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {patterns.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800 text-xs">{p.name}</div>
                    {p.description && (
                      <div className="text-xs text-slate-400 truncate max-w-[160px]" title={p.description}>
                        {p.description}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <code className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-mono break-all max-w-[200px] block">
                      {p.pattern}
                    </code>
                  </td>
                  <td className="px-3 py-2">
                    <code className="text-xs bg-slate-100 text-crimson-red-600 px-1.5 py-0.5 rounded">
                      {p.replacement}
                    </code>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      p.isEnabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {p.isEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{p.createdBy}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button
                        className="px-2 py-1 rounded text-[11px] bg-slate-100 text-slate-700 hover:bg-slate-200"
                        onClick={() => openEdit(p)}
                        aria-label={`Edit pattern ${p.name}`}
                      >
                        Edit
                      </button>
                      <button
                        className={`px-2 py-1 rounded text-[11px] ${
                          p.isEnabled
                            ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            : "bg-green-100 text-green-700 hover:bg-green-200"
                        }`}
                        onClick={() => handleToggleEnabled(p)}
                        aria-label={p.isEnabled ? `Disable pattern ${p.name}` : `Enable pattern ${p.name}`}
                      >
                        {p.isEnabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        className="px-2 py-1 rounded text-[11px] bg-red-100 text-red-700 hover:bg-red-200"
                        onClick={() => handleDelete(p)}
                        aria-label={`Delete pattern ${p.name}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <PatternModal
          existing={editing}
          api={api}
          toastAlert={toastAlert}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchPatterns(); }}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Add / Edit pattern modal
// ─────────────────────────────────────────────────────────────────

function PatternModal({
  existing,
  api,
  toastAlert,
  onClose,
  onSaved,
}: {
  existing: CustomPattern | null;
  api: ReturnType<typeof useApi>;
  toastAlert: ReturnType<typeof useToastAlert>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [pattern, setPattern] = useState(existing?.pattern ?? "");
  const [replacement, setReplacement] = useState(existing?.replacement ?? "[redacted]");
  const [isEnabled, setIsEnabled] = useState(existing?.isEnabled ?? true);
  const [patternError, setPatternError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Client-side regex validation
  const validatePattern = (p: string) => {
    if (!p) { setPatternError(""); return; }
    try {
      new RegExp(p);
      setPatternError("");
    } catch (e: any) {
      setPatternError(e.message ?? "Invalid regex");
    }
  };

  const handlePatternChange = (val: string) => {
    setPattern(val);
    validatePattern(val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (patternError) return;
    setSubmitting(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        pattern: pattern.trim(),
        replacement: replacement.trim() || "[redacted]",
        isEnabled,
      };

      const res = isEdit
        ? await api.admin.put(`Scrubber/patterns/${existing!.id}`, body)
        : await api.admin.post("Scrubber/patterns", body);

      if (!res.ok) {
        const data = await res.json();
        const msg = data?.error ?? (isEdit ? "Failed to update pattern" : "Failed to create pattern");
        toastAlert.displayToast(Severity.Error, msg);
        // If the server returned a regex error, surface it inline
        if (data?.error?.toLowerCase().includes("regex") || data?.error?.toLowerCase().includes("invalid")) {
          setPatternError(data.error);
        }
        return;
      }

      toastAlert.displayToast(Severity.Success, isEdit ? "Pattern updated" : "Pattern created");
      onSaved();
    } catch {
      toastAlert.displayToast(Severity.Error, isEdit ? "Failed to update pattern" : "Failed to create pattern");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      aria-label={isEdit ? "Edit scrubber pattern" : "Add scrubber pattern"}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isEdit ? "Edit Pattern" : "Add Custom Pattern"}</h3>
          <button onClick={onClose} aria-label="Close dialog" className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="patternName" className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="patternName" type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Employee ID" required autoFocus maxLength={200}
            />
          </div>

          <div>
            <label htmlFor="patternDesc" className="block text-sm font-medium text-slate-700 mb-1">
              Description <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              id="patternDesc" type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this pattern match?" maxLength={500}
            />
          </div>

          <div>
            <label htmlFor="patternRegex" className="block text-sm font-medium text-slate-700 mb-1">
              Regex Pattern <span className="text-red-500">*</span>
            </label>
            <input
              id="patternRegex" type="text"
              className={`w-full px-3 py-2 border rounded-lg text-sm font-mono ${
                patternError ? "border-red-400 bg-red-50" : "border-slate-300"
              }`}
              value={pattern} onChange={(e) => handlePatternChange(e.target.value)}
              placeholder="e.g. \bEMP-\d{6}\b" required
            />
            {patternError ? (
              <p className="text-xs text-red-600 mt-1">{patternError}</p>
            ) : (
              <p className="text-xs text-slate-400 mt-1">
                Standard JavaScript/PCRE regex. Test at{" "}
                <a href="https://regex101.com" target="_blank" rel="noopener noreferrer" className="text-cornflower-blue-500 underline">
                  regex101.com
                </a>{" "}
                before saving.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="patternReplacement" className="block text-sm font-medium text-slate-700 mb-1">
              Replacement Text <span className="text-red-500">*</span>
            </label>
            <input
              id="patternReplacement" type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
              value={replacement} onChange={(e) => setReplacement(e.target.value)}
              placeholder="e.g. [redacted employee-id]" required maxLength={200}
            />
            <p className="text-xs text-slate-400 mt-1">
              The matched text is replaced with this string in stored and displayed messages.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox" id="patternEnabled"
              className="w-4 h-4 rounded border-slate-300 text-cornflower-blue-600"
              checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)}
            />
            <label htmlFor="patternEnabled" className="text-sm text-slate-700">
              Enable this pattern immediately
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50"
              disabled={submitting || !name.trim() || !pattern.trim() || !!patternError}
            >
              {submitting ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save Changes" : "Create Pattern")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
